import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Button,
} from "@mui/material";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { formatRentValues } from "../../../helpers/formatingFunctions";
import UpdateRentPopup from "../UpdateRentPopup/UpdateRentPopup";
import DriverInfoModal from "../DriverInfoModal/DriverInfoModal";
import OwnerCompanyInfoModal from "../OwnerCompanyInfoModal/OwnerCompanyInfoModal";
import RentPopup from "../RentPopup/RentPopup";

interface RentalInformationProps {
  asset: any;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const RentalInformation: React.FC<RentalInformationProps> = ({
  asset,
  isLoading,
  isError,
  error,
}) => {
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [authorityCompanyModalOpen, setAuthorityCompanyModalOpen] =
    useState(false);
  const [selectedAuthorityCompanyId, setSelectedAuthorityCompanyId] = useState<
    string | null
  >(null);
  const [billToModalOpen, setBillToModalOpen] = useState(false);
  const [selectedBillToId, setSelectedBillToId] = useState<string | null>(null);
  const [soldToModalOpen, setSoldToModalOpen] = useState(false);
  const [selectedSoldToId, setSelectedSoldToId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
      >
        <Typography>Error: {error?.message}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 3 },
        maxHeight: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        className="asset-detail-field"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          Rental Information
        </Typography>
        {asset && <UpdateRentPopup rent={asset} />}
      </Box>

      {asset === null ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Typography variant="body1" color="text.secondary" align="center">
            This asset is not rented
          </Typography>
          <RentPopup />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {Object.entries(asset)
            .filter(
              ([key]) =>
                ![
                  "id",
                  "truckId",
                  "trailerId",
                  "billToId",
                  "driverId",
                  "authorityCompanyId",
                  "soldToId",
                ].includes(key),
            )
            .map(([key, val]) => {
              if (key === "driverFullName") {
                return (
                  <Grid item xs={12} key={key}>
                    <Box
                      className="asset-detail-field"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {camelCaseToSentenceCase(key)}:
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => {
                          setSelectedDriverId(asset.driverId);
                          setDriverModalOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {val as string}
                      </Button>
                    </Box>
                  </Grid>
                );
              }

              if (key === "authorityCompanyName") {
                return (
                  <Grid item xs={12} key={key}>
                    <Box
                      className="asset-detail-field"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {camelCaseToSentenceCase(key)}:
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => {
                          setSelectedAuthorityCompanyId(
                            asset.authorityCompanyId,
                          );
                          setAuthorityCompanyModalOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {val as string}
                      </Button>
                    </Box>
                  </Grid>
                );
              }

              if (key === "soldToCompanyName") {
                return (
                  <Grid item xs={12} key={key}>
                    <Box
                      className="asset-detail-field"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {camelCaseToSentenceCase(key)}:
                      </Typography>
                      <Button
                        variant="text"
                        disabled={!asset.soldToId}
                        onClick={() => {
                          setSelectedSoldToId(asset.soldToId);
                          setSoldToModalOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {(val as string) || "-"}
                      </Button>
                    </Box>
                  </Grid>
                );
              }

              if (key === "billToCompanyName") {
                return (
                  <Grid item xs={12} key={key}>
                    <Box
                      className="asset-detail-field"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {camelCaseToSentenceCase(key)}:
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => {
                          setSelectedBillToId(asset.billToId);
                          setBillToModalOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {val as string}
                      </Button>
                    </Box>
                  </Grid>
                );
              }
              if (key === "downPayment") {
                key = "reimbursableDeposit";
              }

              return (
                <Grid item xs={12} key={key}>
                  <Box
                    className="asset-detail-field"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderRadius: 1,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {camelCaseToSentenceCase(key)}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {formatRentValues(
                        key,
                        val as any,
                        asset?.billingFrequency,
                      )}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
        </Grid>
      )}

      <DriverInfoModal
        id={selectedDriverId || ""}
        open={driverModalOpen}
        onClose={() => setDriverModalOpen(false)}
      />

      <OwnerCompanyInfoModal
        id={selectedAuthorityCompanyId || ""}
        open={authorityCompanyModalOpen}
        onClose={() => setAuthorityCompanyModalOpen(false)}
      />

      <OwnerCompanyInfoModal
        id={selectedBillToId || ""}
        open={billToModalOpen}
        onClose={() => setBillToModalOpen(false)}
      />

      <OwnerCompanyInfoModal
        id={selectedSoldToId || ""}
        open={soldToModalOpen}
        onClose={() => setSoldToModalOpen(false)}
      />
    </Paper>
  );
};

export default RentalInformation;
